import React, { useState } from 'react';
import { useLocale } from '../../contexts/LocaleContext';
import { Organization } from '../../contexts/AuthContext';
import './SharedComponents.css';

interface CloneOrgModalProps {
  org: Organization;
  onClone: (newName: string, includeEmployees: boolean) => Promise<void>;
  onClose: () => void;
}

export const CloneOrgModal: React.FC<CloneOrgModalProps> = ({ org, onClone, onClose }) => {
  const { t } = useLocale();
  const [cloneNewName, setCloneNewName] = useState(`${org.name} (Copy)`);
  const [cloneIncludeEmployees, setCloneIncludeEmployees] = useState(false);
  const [isCloning, setIsCloning] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cloneNewName.trim()) return;

    setIsCloning(true);
    try {
      await onClone(cloneNewName.trim(), cloneIncludeEmployees);
    } catch (error) {
      // Error handling is done by parent
    } finally {
      setIsCloning(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={() => !isCloning && onClose()}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('admin.cloneOrganization')}</h2>
          <button className="modal-close" onClick={() => !isCloning && onClose()}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <p className="modal-description">
              {t('firm.cloneDescription', { name: org.name })}
            </p>
            <div className="form-row">
              <label>{t('admin.newOrgName')}</label>
              <input
                type="text"
                value={cloneNewName}
                onChange={(e) => setCloneNewName(e.target.value)}
                placeholder={t('admin.enterNewName')}
                required
                disabled={isCloning}
              />
            </div>
            <div className="checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={cloneIncludeEmployees}
                  onChange={(e) => setCloneIncludeEmployees(e.target.checked)}
                  disabled={isCloning}
                />
                <span>{t('admin.includeEmployees')}</span>
              </label>
              <p className="help-text">
                {cloneIncludeEmployees
                  ? t('admin.includeEmployeesHelp')
                  : t('admin.excludeEmployeesHelp')}
              </p>
            </div>
          </div>
          <div className="modal-footer">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={isCloning}
            >
              {t('general.cancel')}
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isCloning || !cloneNewName.trim()}
            >
              {isCloning ? t('admin.cloning') : t('admin.cloneAction')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CloneOrgModal;
